import React, { FC, useEffect, useRef, useState } from 'react'
import './style.scss'
import CameraIcon from '@mui/icons-material/CameraAlt'
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner'
import Button from '@mui/material/Button'
import jsQR from 'jsqr'

type CameraProps = {
  text: string
  deviceId: string
  onSubmitImage: (value: File) => void
} & (
  | { readQr?: false; callback?: never }
  | { readQr: true; callback: (qrValue: string) => Promise<any> }
)

const QR_SCAN_INTERVAL_MS = 200
const QR_SCAN_DURATION_MS = 5 * 60 * 1000

const stopTracks = (stream: MediaStream) => {
  const tracks = stream.getTracks()
  tracks.forEach(track => track.stop())
}

const NewCamera: FC<CameraProps> = ({
  deviceId,
  text,
  onSubmitImage,
  readQr = false,
  callback
}) => {
  const imageRef = useRef<HTMLImageElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const scanIntervalRef = useRef<number | null>(null)
  const scanTimeoutRef = useRef<number | null>(null)
  const [taken, setTaken] = useState(false)
  const [scanning, setScanning] = useState(false)

  const stopQrScan = () => {
    if (scanIntervalRef.current !== null) {
      window.clearInterval(scanIntervalRef.current)
      scanIntervalRef.current = null
    }
    if (scanTimeoutRef.current !== null) {
      window.clearTimeout(scanTimeoutRef.current)
      scanTimeoutRef.current = null
    }
    setScanning(false)
  }

  const scanFrame = () => {
    const video = videoRef.current
    if (!video || video.readyState !== video.HAVE_ENOUGH_DATA) return
    const canvas = document.createElement('canvas')
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
    const code = jsQR(imageData.data, imageData.width, imageData.height)
    if (code && code.data) {
      stopQrScan()
      if (readQr && callback) {
        callback(code.data)
      }
    }
  }

  const startQrScan = () => {
    if (scanning) return
    setScanning(true)
    scanIntervalRef.current = window.setInterval(scanFrame, QR_SCAN_INTERVAL_MS)
    scanTimeoutRef.current = window.setTimeout(() => {
      console.log('Tiempo de escaneo de QR finalizado sin detección')
      stopQrScan()
    }, QR_SCAN_DURATION_MS)
  }

  useEffect(() => {
    return () => {
      stopQrScan()
    }
  }, [])

  useEffect(() => {
    let stream: MediaStream | null = null
    const initializeCamera = async () => {
      try {
        if (
          !navigator.mediaDevices ||
          typeof navigator.mediaDevices.getUserMedia !== 'function'
        ) {
          console.warn('getUserMedia no está disponible en este navegador.')
          return
        }

        const constraints: MediaStreamConstraints = {
          video: {
            deviceId: deviceId ? { exact: deviceId } : undefined
          }
        }

        stream = await navigator.mediaDevices.getUserMedia(constraints)

        const videoElement = videoRef.current
        if (videoElement) {
          videoElement.srcObject = stream
          const playPromise = videoElement.play()
          if (playPromise !== undefined) {
            try {
              await playPromise
            } catch (error) {
              console.error('error al reproducir camaras local')
            }
          }
        }
      } catch (error) {
        console.error('Error al inicializar la cámara:', error)
      }
    }

    const cleanupCamera = () => {
      if (stream) {
        stopTracks(stream)
      }
    }

    initializeCamera()

    return () => {
      cleanupCamera()
    }
  }, [deviceId])

  const captureImage = () => {
    stopQrScan()
    const videoElement = videoRef.current
    const imageElement = imageRef.current
    if (videoElement && imageElement) {
      const canvas = document.createElement('canvas')
      const context = canvas.getContext('2d')

      if (context) {
        canvas.width = videoElement.videoWidth
        canvas.height = videoElement.videoHeight
        context.drawImage(videoElement, 0, 0, canvas.width, canvas.height)
        imageElement.src = canvas.toDataURL('image/png')
        canvas.toBlob((blob: Blob | null) => {
          if (blob) {
            const file = new File([blob], 'captured-image.png')
            onSubmitImage(file)
          }
        }, 'image/png')

        setTaken(true)
      }
    }
  }

  const retakePhoto = () => {
    setTaken(false)
  }

  return (
    <div className="camera-section">
      <p className="textoCamera">{text}</p>
      <video
        ref={videoRef}
        width="100%"
        height="100%"
        autoPlay
        style={{ display: taken ? 'none' : 'inline' }}
      ></video>
      <img
        ref={imageRef}
        width="100%"
        height="100%"
        alt={taken ? 'Imagen capturada desde la cámara' : ''}
        style={{ display: !taken ? 'none' : 'inline' }}
      />
      {!taken && (
        <div className="camera-buttons">
          <Button
            variant="contained"
            className={`capture-button`}
            startIcon={<CameraIcon />}
            onClick={captureImage}
          >
            Tomar foto
          </Button>
          {readQr && (
            <Button
              variant="contained"
              className={`qr-button`}
              startIcon={<QrCodeScannerIcon />}
              onClick={startQrScan}
              disabled={scanning}
            >
              {scanning ? 'Escaneando...' : 'Leer QR'}
            </Button>
          )}
        </div>
      )}
      {taken && (
        <Button
          startIcon={<CameraIcon />}
          variant="contained"
          className={`capture-button`}
          onClick={retakePhoto}
        >
          Reintentar
        </Button>
      )}
    </div>
  )
}

export default NewCamera
